import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollAnimations = () => {
  const location = useLocation();

  useEffect(() => {
    // Mark that JS has loaded - this enables the animation hiding
    document.body.classList.add('js-loaded');
    
    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';

    // Small delay to ensure DOM is ready after route change
    const initTimeout = setTimeout(() => {
      // Intersection Observer for fade-in animations
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');

            // Add staggered animation to children if they have the class
            const children = entry.target.querySelectorAll('.animate-on-scroll');
            children.forEach((child, index) => {
              setTimeout(() => {
                child.classList.add('animate-in');
              }, index * 100);
            });
          }
        });
      }, observerOptions);

      // Observe all elements with animation classes
      const animatedElements = document.querySelectorAll('.fade-in-section, .slide-in-left, .slide-in-right, .scale-in');
      animatedElements.forEach((el) => {
        // Remove previous animate-in class for fresh observation on route change
        el.classList.remove('animate-in');
        observer.observe(el);
      });

      // Also handle animate-on-scroll elements
      const scrollElements = document.querySelectorAll('.animate-on-scroll');
      scrollElements.forEach((el) => {
        el.classList.remove('animate-in');
      });

      // Store observer for cleanup
      window._scrollAnimationObserver = observer;
    }, 50);

    // Parallax effect (disabled on mobile for performance)
    const handleScroll = () => {
      // Disable parallax on mobile devices (< 768px)
      if (window.innerWidth < 768) return;

      const scrolled = window.pageYOffset;
      const parallaxElements = document.querySelectorAll('.parallax');

      parallaxElements.forEach((element) => {
        const speed = element.dataset.speed || 0.5;
        const yPos = -(scrolled * speed);
        element.style.transform = `translateY(${yPos}px)`;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(initTimeout);
      if (window._scrollAnimationObserver) {
        window._scrollAnimationObserver.disconnect();
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]); // Re-run when route changes

  return (
    <style>{`
      /* Smooth scrolling */
      html {
        scroll-behavior: smooth;
      }

      /* Base animation states - content visible by default */
      .fade-in-section,
      .slide-in-left,
      .slide-in-right,
      .scale-in,
      .animate-on-scroll {
        opacity: 1;
        transition: opacity 0.8s ease-out, transform 0.8s ease-out;
      }

      /* Only apply initial hidden state after JS has loaded */
      .js-loaded .fade-in-section:not(.animate-in),
      .js-loaded .slide-in-left:not(.animate-in),
      .js-loaded .slide-in-right:not(.animate-in),
      .js-loaded .scale-in:not(.animate-in),
      .js-loaded .animate-on-scroll:not(.animate-in) {
        opacity: 0;
      }

      .js-loaded .fade-in-section:not(.animate-in) {
        transform: translateY(30px);
      }

      .js-loaded .slide-in-left:not(.animate-in) {
        transform: translateX(-50px);
      }

      .js-loaded .slide-in-right:not(.animate-in) {
        transform: translateX(50px);
      }

      .js-loaded .scale-in:not(.animate-in) {
        transform: scale(0.9);
      }

      /* Animated state */
      .animate-in {
        opacity: 1 !important;
        transform: translateY(0) translateX(0) scale(1) !important;
      }

      /* Parallax container */
      .parallax {
        transition: transform 0.5s cubic-bezier(0.33, 1, 0.68, 1);
        will-change: transform;
      }

      /* Page transition */
      .page-transition {
        animation: fadeIn 0.6s ease-in-out;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Stagger children animation */
      .stagger-container .animate-on-scroll {
        transition-delay: calc(var(--stagger-index, 0) * 0.1s);
      }

      /* Disable hover effects on touch devices */
      @media (hover: none) and (pointer: coarse) {
        .service-card:hover,
        .process-card:hover,
        .help-item:hover,
        .aboutus-img:hover {
          transform: none !important;
        }
      }

      /* Reduce motion for accessibility */
      @media (prefers-reduced-motion: reduce) {
        html {
          scroll-behavior: auto;
        }

        .fade-in-section,
        .slide-in-left,
        .slide-in-right,
        .scale-in,
        .animate-on-scroll,
        .parallax {
          animation: none !important;
          transition: none !important;
          opacity: 1 !important;
          transform: none !important;
        }
      }

      /* Disable parallax on mobile */
      @media (max-width: 767.98px) {
        .parallax {
          transform: none !important;
        }
      }
    `}</style>
  );
};

export default ScrollAnimations;
